import React from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import AuthLayout from "../../layouts/AuthLayout";
import FormField from "../../components/FormField";
import { ATP_LOGIN, ATP_REGISTER, phoneRegExp, USERS_TYPE } from "../../data/Constants";
import RippleButton from "../../components/buttons/RippleButton";
import { registerUser } from "../../redux/slices/authSlice";

const AtpSignUp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading } = useSelector((state) => state.auth);
    const location = useLocation();

    // Initial form values
    const initialValues = {
        name: "",
        company_name: "",
        email: "",
        telephone: "",
        password: "",
        confirmPassword: "",
    };

    // Form validation schema using Yup
    const validationSchema = Yup.object({
        name: Yup.string().max(80, "Name must be at most 80 characters").required("Name is required"),

        company_name: Yup.string().max(100, "Organization Name must be at most 100 characters").required("Organization Name is required"),
        email: Yup.string()
            .email("Invalid email address")
            .required("Email is required")
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email address"),
        telephone: Yup.string().matches(phoneRegExp, "Telephone is not valid").required("Telephone is required"),
        password: Yup.string()
            .matches(/^(?!\s)/, "Password cannot start with space")
            .min(8, "Password must be at least 8 characters")
            .matches(/[0-9]/, "Password requires a number")
            .matches(/[a-z]/, "Password requires a lowercase letter")
            .matches(/[A-Z]/, "Password requires an uppercase letter")
            .matches(/[\W_]/, "Password requires a symbol")
            .matches(/(?<!\s)$/, "Password cannot end with space")
            .required("Password is required"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm Password is required"),
    });

    return (
        <AuthLayout rightContainerClassName="justify-content-center mt-lg-0 my-4">
            <section>
                {/*Header for entering account details */}
                <h5 className="fs-22 fw-600 text-gray-950 auth-registration-title-2">Enter Training Provider Details</h5>
            </section>
            <section className="auth-registration-form">
                {/* Formik form state and submission */}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, { resetForm }) => {
                        const newUser = { ...values };
                        newUser["user_type"] = USERS_TYPE.ATP_USER;
                        delete newUser.confirmPassword;
                        dispatch(registerUser({ newUser, navigate }));
                        resetForm();
                    }}
                >
                    {() => (
                        <Form className="row">
                            <FormField label="Your Name" name="name" type="type" fieldSize="col-lg-6 col-12" />
                            <FormField label="Organization Name" name="company_name" type="type" fieldSize="col-lg-6 col-12" />
                            <FormField label="Email (will use as User ID)" name="email" type="text" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your email address" />
                            <FormField label="Telephone Number/Office Number" name="telephone" type="text" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your Telephone/Office Number" />
                            <FormField label="Create Password" name="password" type="password" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your password" />
                            <FormField label="Confirm Password" name="confirmPassword" type="password" fieldSize="col-lg-6 col-12" footerPlaceholder="Please confirm your  password" />

                            <div className="d-flex justify-content-between align-items-center auth-login-button">
                                {location.pathname.includes(ATP_REGISTER) ? (
                                    <p className="fw-500 fs-16 mb-0">
                                        Already have an Account?{" "}
                                        <Link className="text-primary-sapphire cursor-pointer text-decoration-none fw-600" to={ATP_LOGIN}>
                                            Sign In
                                        </Link>
                                    </p>
                                ) : (
                                    ""
                                )}
                                <RippleButton type="submit" isLoading={loading}>
                                    {t("COMMON.REGISTER")}
                                </RippleButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>
        </AuthLayout>
    );
};

export default AtpSignUp;
